import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChalkboardTeacher, FilePlus, Users, BookOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TeacherSpace() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [contents, setContents] = useState([]);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ title: "", exam_id: "", subject: "", type: "cours", content: "" });
  const [saving, setSaving] = useState(false);
  const [enrollCode, setEnrollCode] = useState("");
  const [devoirs, setDevoirs] = useState([]);
  const [devoirForm, setDevoirForm] = useState({ title: "", exam_id: "", subject: "", due_at: "" });

  const loadContents = () => api.get("/teacher/contents").then((r) => setContents(r.data)).catch(() => {});
  const loadStudents = () => api.get("/teacher/students").then((r) => setStudents(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/exams").then((r) => setExams(r.data));
    loadContents();
    loadStudents();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const enroll = async (e) => {
    e.preventDefault();
    if (!enrollCode.trim()) return;
    try {
      await api.post("/teacher/enroll", { code: enrollCode });
      toast.success("Élève ajouté à votre liste !");
      setEnrollCode("");
      loadStudents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadDevoirs = () => api.get("/teacher/devoirs").then((r) => setDevoirs(r.data)).catch(() => {});
  useEffect(() => { loadDevoirs(); }, []);

  const createDevoir = async (e) => {
    e.preventDefault();
    const { title, exam_id, subject, due_at } = devoirForm;
    if (!title || !exam_id || !subject) { toast.error("Titre, examen et matière requis."); return; }
    try {
      const r = await api.post("/teacher/devoirs", { quiz_id: `${exam_id}__${subject}`, title, due_at: due_at || null });
      toast.success(`Devoir créé et assigné à ${r.data.assigned} élève(s).`);
      setDevoirForm({ title: "", exam_id: "", subject: "", due_at: "" });
      loadDevoirs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.exam_id || !form.subject || !form.title) { toast.error("Remplissez tous les champs."); return; }
    setSaving(true);
    try {
      await api.post("/teacher/contents", form);
      toast.success("Contenu publié !");
      setForm({ title: "", exam_id: "", subject: "", type: "cours", content: "" });
      loadContents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Espace enseignant</h1>
        <p className="mt-1 text-ink/65">Créez des contenus et suivez vos élèves, {user?.name?.split(" ")[0]}.</p>
      </div>

      <Tabs defaultValue="create">
        <TabsList className="rounded-full bg-sand">
          <TabsTrigger value="create" data-testid="tab-create" className="rounded-full">Créer un contenu</TabsTrigger>
          <TabsTrigger value="contents" data-testid="tab-contents" className="rounded-full">Mes contenus</TabsTrigger>
          <TabsTrigger value="students" data-testid="tab-students" className="rounded-full">Mes élèves</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-white p-6">
            <div className="flex items-center gap-2"><FilePlus weight="fill" className="text-terracotta" size={22} /><h2 className="font-display text-lg font-bold">Nouveau cours / document</h2></div>
            <Input data-testid="content-title" value={form.title} onChange={set("title")} placeholder="Titre du contenu" className="rounded-xl" />
            <div className="grid gap-3 sm:grid-cols-3">
              <select data-testid="content-exam" value={form.exam_id} onChange={set("exam_id")} className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm">
                <option value="">Examen…</option>
                {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <Input data-testid="content-subject" value={form.subject} onChange={set("subject")} placeholder="Matière" className="rounded-xl" />
              <select data-testid="content-type" value={form.type} onChange={set("type")} className="rounded-xl border border-border bg-white px-3 py-2.5 text-sm">
                <option value="cours">Cours</option><option value="methode">Méthode</option><option value="synthese">Synthèse</option>
              </select>
            </div>
            <Textarea data-testid="content-body" value={form.content} onChange={set("content")} rows={6} placeholder="Contenu (Markdown supporté)…" className="rounded-xl" />
            <Button data-testid="content-submit" type="submit" disabled={saving} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">
              {saving ? "Publication…" : "Publier le contenu"}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="contents">
          <div className="space-y-3">
            {contents.length ? contents.map((c) => (
              <div key={c.id} data-testid={`tcontent-${c.id}`} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-4">
                <BookOpen className="text-terracotta" size={22} />
                <div className="flex-1">
                  <div className="font-semibold">{c.title}</div>
                  <div className="text-xs text-muted-foreground">{c.subject} · {c.type}</div>
                </div>
              </div>
            )) : <p className="py-8 text-center text-sm text-muted-foreground">Aucun contenu publié pour l'instant.</p>}
          </div>
        </TabsContent>

        <TabsContent value="students">
          <form onSubmit={enroll} className="mb-4 flex flex-col gap-3 rounded-2xl border border-border bg-white p-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 text-sm font-semibold text-forest"><Users weight="bold" /> Ajouter un élève :</div>
            <Input data-testid="enroll-code" value={enrollCode} onChange={(e) => setEnrollCode(e.target.value)}
              placeholder="code de partage de l'élève (ex : YAYA01)" className="flex-1 rounded-xl" />
            <Button data-testid="enroll-btn" type="submit" className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">Ajouter</Button>
          </form>

          <form onSubmit={createDevoir} data-testid="devoir-form" className="mb-4 rounded-2xl border border-forest/30 bg-forest/5 p-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-forest"><FilePlus weight="bold" /> Assigner un devoir encadré</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="devoir-title" value={devoirForm.title} onChange={(e) => setDevoirForm({ ...devoirForm, title: e.target.value })} placeholder="Titre du devoir" className="rounded-xl" />
              <input data-testid="devoir-due" type="date" value={devoirForm.due_at} onChange={(e) => setDevoirForm({ ...devoirForm, due_at: e.target.value })} className="rounded-xl border border-border bg-white px-3 py-2 text-sm" />
              <select data-testid="devoir-exam" value={devoirForm.exam_id} onChange={(e) => setDevoirForm({ ...devoirForm, exam_id: e.target.value })} className="rounded-xl border border-border bg-white px-3 py-2 text-sm">
                <option value="">Examen…</option>
                {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
              </select>
              <Input data-testid="devoir-subject" value={devoirForm.subject} onChange={(e) => setDevoirForm({ ...devoirForm, subject: e.target.value })} placeholder="Matière (ex : Mathématiques)" className="rounded-xl" />
            </div>
            <Button data-testid="devoir-create-btn" type="submit" className="mt-3 rounded-full bg-forest text-white hover:bg-forest/90">Créer & assigner</Button>
          </form>
          {devoirs.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="teacher-devoirs">
              {devoirs.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-2.5 text-sm">
                  <span className="font-medium">{d.title}</span>
                  <span className="text-xs text-muted-foreground">{d.roster?.length ?? 0} élève(s){d.due_at ? ` · ${new Date(d.due_at).toLocaleDateString("fr-FR")}` : ""}</span>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-2xl border border-border bg-white p-4">
            <div className="mb-3 flex items-center gap-2"><Users weight="fill" className="text-forest" size={20} /><h2 className="font-display font-bold">Élèves ({students.length})</h2></div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Examen</TableHead><TableHead>Quiz</TableHead><TableHead>XP</TableHead></TableRow></TableHeader>
                <TableBody>
                  {students.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.exam}</TableCell>
                      <TableCell>{s.quizzes_done}</TableCell>
                      <TableCell>{s.xp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
